"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="11028" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-base font-semibold text-foreground/90 transition-colors hover:text-primary py-2 block group" data-index={i}>
                {item.label}
                <span className="absolute bottom-0 left-0 h-[3px] w-full origin-bottom-right scale-x-0 bg-primary transition-transform duration-300 group-hover:origin-bottom-left group-hover:scale-x-100 rounded-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-bold text-foreground hover:text-primary transition-colors border-b border-border/50 pb-2" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="pt-4 flex flex-col gap-3">
        <Button variant="ghost" className="w-full justify-start text-base font-semibold" asChild>
          <Link to="/OnlineAppPage">登录</Link>
        </Button>
        <Button className="w-full bg-primary hover:bg-primary/90 text-white rounded-full font-bold shadow-md" asChild>
          <Link to="/OnlineAppPage">开始使用</Link>
        </Button>
      </div>
    </nav>;
}
export default function NavigationBar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="导航栏" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b bg-background text-foreground ${isScrolled ? "bg-background/90 backdrop-blur-xl border-border shadow-md" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-4 flex items-center justify-between !h-[100px] md:px-6">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block">
            <img src="https://native-mackerel.10web.cloud/wp-content/uploads/2026/04/闪电耳机图标设计-4.png" alt="听力通 Logo" className="w-[160px] h-auto object-contain !m-0 md:w-[200px]" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="16" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-4">
          <Button variant="ghost" className="text-foreground/90 font-semibold hover:text-primary" asChild>
            <Link to="/OnlineAppPage">登录</Link>
          </Button>
          <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all active:translate-y-0" asChild>
            <Link to="/OnlineAppPage">开始使用</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:text-primary">
            {isMobileMenuOpen ? <X className="size-8" /> : <Menu className="size-8" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-background border-b border-border shadow-2xl animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}